/**
 * Pricing Tier Mapping Utilities
 * 
 * Provides translation between different pricing tier naming conventions
 * used across the Redwood Value Platform.
 * 
 * ⚠️  CRITICAL WARNING - DO NOT MODIFY REVENUE BANDS WITHOUT EXPLICIT USER REQUEST ⚠️
 * These revenue bands are the EXACT business tiers and must never be changed:
 * - Emerging: $1B-$5B
 * - Enterprise: $5B-$25B  
 * - Large Enterprise: $25B+
 * Any unauthorized changes will break ROI calculations and pricing lookups.
 */

// Revenue bands collected in Value Engineering environment step
export const VALUE_ENGINEERING_REVENUE_BANDS = [
  "$1B – $5B",
  "$5B – $25B", 
  "> $25B"
] as const;

// Pricing tiers used in platform/pillar pricing database
export const PLATFORM_PRICING_TIERS = [
  "Emerging",
  "Enterprise", 
  "Large Enterprise"
] as const;

// ROI calculation tier names (used in StandardROIPricingScreen)
export const ROI_CALCULATION_TIERS = [
  "emerging",
  "enterprise",
  "large_enterprise"
] as const;

export type ValueEngineeringRevenueBand = typeof VALUE_ENGINEERING_REVENUE_BANDS[number];
export type PlatformPricingTier = typeof PLATFORM_PRICING_TIERS[number];
export type ROICalculationTier = typeof ROI_CALCULATION_TIERS[number];

/**
 * Maps Value Engineering revenue bands to Platform pricing tiers
 */
export const REVENUE_BAND_TO_PLATFORM_TIER_MAP: Record<ValueEngineeringRevenueBand, PlatformPricingTier> = {
  "$1B – $5B": "Emerging",
  "$5B – $25B": "Enterprise",
  "> $25B": "Large Enterprise"
};

/**
 * Maps Platform pricing tiers to ROI calculation tier names (lowercase)
 */
export const PLATFORM_TIER_TO_ROI_TIER_MAP: Record<PlatformPricingTier, ROICalculationTier> = {
  "Emerging": "emerging",
  "Enterprise": "enterprise", 
  "Large Enterprise": "large_enterprise"
};

/**
 * Maps Value Engineering revenue bands directly to ROI calculation tier names
 */
export const REVENUE_BAND_TO_ROI_TIER_MAP: Record<ValueEngineeringRevenueBand, ROICalculationTier> = {
  "$1B – $5B": "emerging",
  "$5B – $25B": "enterprise",
  "> $25B": "large_enterprise"
};

/**
 * Reverse mapping: ROI calculation tiers to Platform pricing tiers
 */
export const ROI_TIER_TO_PLATFORM_TIER_MAP: Record<ROICalculationTier, PlatformPricingTier> = {
  "emerging": "Emerging",
  "enterprise": "Enterprise",
  "large_enterprise": "Large Enterprise"
};

/**
 * Estimated revenue ranges for each tier (used in ROI calculations)
 */
export const REVENUE_ESTIMATES: Record<ROICalculationTier, number> = {
  "emerging": 3_000_000_000,   // $3B (mid-range for $1B-$5B)
  "enterprise": 15_000_000_000, // $15B (mid-range for $5B-$25B)
  "large_enterprise": 50_000_000_000      // $50B (conservative for > $25B)
};

/**
 * Converts Value Engineering revenue band to Platform pricing tier
 */
export function revenueBandToPlatformTier(revenueBand: string): PlatformPricingTier {
  const normalizedBand = revenueBand.trim() as ValueEngineeringRevenueBand;
  
  if (normalizedBand in REVENUE_BAND_TO_PLATFORM_TIER_MAP) {
    return REVENUE_BAND_TO_PLATFORM_TIER_MAP[normalizedBand];
  }
  
  // Fallback for unknown revenue bands
  console.warn(`Unknown revenue band: ${revenueBand}, defaulting to Enterprise`);
  return "Enterprise";
}

/**
 * Converts Value Engineering revenue band to ROI calculation tier
 */
export function revenueBandToROITier(revenueBand: string): ROICalculationTier {
  const normalizedBand = revenueBand.trim() as ValueEngineeringRevenueBand;
  
  if (normalizedBand in REVENUE_BAND_TO_ROI_TIER_MAP) {
    return REVENUE_BAND_TO_ROI_TIER_MAP[normalizedBand];
  }
  
  // Fallback for unknown revenue bands
  console.warn(`Unknown revenue band: ${revenueBand}, defaulting to enterprise`);
  return "enterprise";
}

/**
 * Converts Platform pricing tier to ROI calculation tier
 */
export function platformTierToROITier(platformTier: string): ROICalculationTier {
  const normalizedTier = platformTier.trim() as PlatformPricingTier;
  
  if (normalizedTier in PLATFORM_TIER_TO_ROI_TIER_MAP) {
    return PLATFORM_TIER_TO_ROI_TIER_MAP[normalizedTier];
  }
  
  // Fallback for unknown platform tiers
  console.warn(`Unknown platform tier: ${platformTier}, defaulting to enterprise`);
  return "enterprise";
}

/**
 * Converts ROI calculation tier to Platform pricing tier
 */
export function roiTierToPlatformTier(roiTier: string): PlatformPricingTier {
  const normalizedTier = roiTier.toLowerCase().trim() as ROICalculationTier;
  
  if (normalizedTier in ROI_TIER_TO_PLATFORM_TIER_MAP) {
    return ROI_TIER_TO_PLATFORM_TIER_MAP[normalizedTier];
  }
  
  // Fallback for unknown ROI tiers
  console.warn(`Unknown ROI tier: ${roiTier}, defaulting to Enterprise`);
  return "Enterprise";
}

/**
 * Gets estimated revenue for a revenue band (used in ROI calculations)
 */
export function getEstimatedRevenue(revenueBand: string): number {
  const roiTier = revenueBandToROITier(revenueBand);
  return REVENUE_ESTIMATES[roiTier];
}

/**
 * Validates if a string is a valid Value Engineering revenue band
 */
export function isValidRevenueBand(value: string): value is ValueEngineeringRevenueBand {
  return VALUE_ENGINEERING_REVENUE_BANDS.includes(value as ValueEngineeringRevenueBand);
}

/**
 * Validates if a string is a valid Platform pricing tier
 */
export function isValidPlatformTier(value: string): value is PlatformPricingTier {
  return PLATFORM_PRICING_TIERS.includes(value as PlatformPricingTier);
}

/**
 * Validates if a string is a valid ROI calculation tier
 */
export function isValidROITier(value: string): value is ROICalculationTier {
  return ROI_CALCULATION_TIERS.includes(value.toLowerCase() as ROICalculationTier);
}

/**
 * Debug utility to log current tier mapping
 */
export function debugTierMapping(input: string, context: string = ""): void {
  console.log(`[Tier Mapping${context ? ` - ${context}` : ""}]`, {
    input,
    isRevenueBand: isValidRevenueBand(input),
    isPlatformTier: isValidPlatformTier(input),
    isROITier: isValidROITier(input),
    toPlatformTier: isValidRevenueBand(input) ? revenueBandToPlatformTier(input) : "N/A",
    toROITier: isValidRevenueBand(input) ? revenueBandToROITier(input) : "N/A",
    estimatedRevenue: isValidRevenueBand(input) ? getEstimatedRevenue(input) : "N/A"
  });
}